import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import { TrashIcon } from '../../components/icons/TrashIcon'
import { navigate } from '@reach/router'
import { useTranslation } from 'react-i18next'
import { getProtocolContainer } from '../../container/protocol-module'
import { IProtocolService, PROTOCOL_SERVICE_KEY } from '../../modules/protocols'
import {
  MEASURED_PARAMETERS,
  Protocol,
  ProtocolStatus,
  Rule,
  RuleOperator,
  RuleValueType,
  RuleSet,
  Severity,
} from '../../modules/protocols/models/Protocol'
import { RuleEditor } from './RuleEditor'
import { PageLayout } from 'pages/layout'
import { AppTable, Field } from '../../components/table'
import { CustomModal } from '../../components/modal/CustomModal'
import { ROUTE_PROTOCOLS } from '../../routes/routes-constants'
import { operatorLabel, parameterLabel, severityLabel, valueTypeLabel } from './labels'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { v4 as uuidv4 } from 'uuid'
import { IPatologyService, PATOLOGY_SERVICE_KEY } from '../../modules/patologies'
import { getPatologyContainer } from '../../container/patologies-module'
import { Query } from '../../common/api/Query'

const protocolService = getProtocolContainer().get<IProtocolService>(PROTOCOL_SERVICE_KEY)
const patologyService = getPatologyContainer().get<IPatologyService>(PATOLOGY_SERVICE_KEY)

const ROWS_PER_PAGE = 5

// The model groups rules by set; the table shows them flat, keeping the set each
// one belongs to for the "Rule set" and "Rule n" columns.
type FlatRule = {
  id: string
  ruleSetIndex: number
  ruleIndex: number
  ruleSetName: string
  severity: Severity
  rule: Rule
}

// There is no updateRule in the service: an existing rule can only be removed
// and created again, so saved rows are read-only.
type Row = {
  id: string
  isNewRow: boolean
  ruleSet: string
  ruleNumber: string
  parameter: string
  ruleType: string
  operator: string
  value: string
  sampleSize: string
  severity: string
}

const emptyRow = (): Row => ({
  id: `draft-${uuidv4()}`,
  isNewRow: true,
  ruleSet: '1',
  ruleNumber: '',
  parameter: MEASURED_PARAMETERS[0],
  ruleType: RuleValueType.Num,
  operator: RuleOperator.Greater,
  value: '',
  sampleSize: '1',
  severity: Severity.Warning,
})

const flatten = (ruleSets: RuleSet[]): FlatRule[] =>
  ruleSets.flatMap((rs, i) =>
    (rs.rules || []).map((r, j) => ({
      id: r.id,
      ruleSetIndex: i + 1,
      ruleIndex: j + 1,
      ruleSetName: rs.name,
      severity: rs.severity,
      rule: r,
    }))
  )

type Props = { id?: string }

export function ProtocolDetail(props: Props) {
  const { t } = useTranslation()

  const [protocol, setProtocol] = useState<Protocol | undefined>()
  const [ruleSetName, setRuleSetName] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [severity, setSeverity] = useState<Severity>(Severity.Warning)

  const [page, setPage] = useState<number>(0)
  const [selected, setSelected] = useState<string[]>([])
  const [showEditors, setShowEditors] = useState<boolean>(false)
  const [editingName, setEditingName] = useState<boolean>(false)
  const [name, setName] = useState<string>('')
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false)
  const [disableAnchor, setDisableAnchor] = useState<null | HTMLElement>(null)

  // Pathologies the protocol applies to; patients inherit it from these.
  const [catalog, setCatalog] = useState<{ id: string; name: string }[]>([])
  const [pathologyIDs, setPathologyIDs] = useState<string[]>([])

  const isNew = !props.id
  // Rows being filled in and not saved yet.
  const [draft, setDraft] = useState<Row[]>([])
  const [saving, setSaving] = useState<boolean>(false)
  // Rows that cannot be saved yet because they have no value.
  const [rowsWithError, setRowsWithError] = useState<string[]>([])
  // Protocols are created in the "Protocols" screen: here the pencil only
  // picks an existing one to manage its rules.
  const [selectorAnchor, setSelectorAnchor] = useState<null | HTMLElement>(null)
  const [available, setAvailable] = useState<Protocol[]>([])

  const openSelector = (e: React.MouseEvent<HTMLElement>) => {
    setSelectorAnchor(e.currentTarget)
    protocolService.list().subscribe((ps) => setAvailable(ps || []))
  }

  const selectProtocol = (id: string) => {
    setSelectorAnchor(null)
    if (id !== props.id) navigate(`${ROUTE_PROTOCOLS}/${id}`).then()
  }

  const load = useCallback(() => {
    if (!props.id) return
    protocolService.getByID(props.id).subscribe((p) => {
      setProtocol(p)
      if (p) setName(p.name)
    })
    protocolService
      .pathologies(props.id)
      .subscribe((l) => setPathologyIDs((l || []).map((x) => x.pathologyID)))
  }, [props.id])

  useEffect(() => {
    patologyService
      .getFilteredList(new Query({}))
      .subscribe((res) =>
        setCatalog(
          res
            ? res.items.filter((x) => x.id).map((x) => ({ id: x.id as string, name: x.name }))
            : []
        )
      )
  }, [])

  const savePathologies = (ids: string[]) => {
    if (!props.id) return
    setPathologyIDs(ids)
    protocolService.setPathologies(props.id, ids).subscribe()
  }

  useEffect(load, [load])

  // With no rules, open a blank row so the table is ready to fill in.
  useEffect(() => {
    if (!protocol) return
    const noRules = (protocol.ruleSets || []).every((rs) => (rs.rules || []).length === 0)
    if (protocol.status === ProtocolStatus.Draft && noRules) {
      setDraft((d) => (d.length === 0 ? [emptyRow()] : d))
    }
  }, [protocol])

  const rules = useMemo(() => flatten(protocol?.ruleSets || []), [protocol])

  // Reached from "Protocol Management" with no id: nothing is loaded yet.
  if (!protocol && !isNew) {
    return (
      <PageLayout>
        <Typography color="text.secondary">{t('loading')}</Typography>
      </PageLayout>
    )
  }

  // A protocol is only editable while it is a draft: published is immutable and
  // a new version has to be created. Same rule the backend enforces.
  const editable = protocol?.status === ProtocolStatus.Draft

  const addRuleSet = () => {
    if (!protocol || !ruleSetName.trim() || !message.trim()) return
    protocolService
      .createRuleSet({
        protocolID: protocol.id,
        name: ruleSetName.trim(),
        notificationMessage: message.trim(),
        severity,
      })
      .subscribe(() => {
        setRuleSetName('')
        setMessage('')
        load()
      })
  }

  const addRule = (r: Partial<Rule>) => protocolService.createRule(r).subscribe(() => load())
  const removeRule = (id: string) => protocolService.removeRule(id).subscribe(() => load())
  const removeRuleSet = (id: string) => protocolService.removeRuleSet(id).subscribe(() => load())

  // Renames the loaded protocol; creating one belongs to "Protocols".
  const saveName = () => {
    const trimmed = name.trim()
    setEditingName(false)
    if (!trimmed) return

    if (!protocol || trimmed === protocol.name) return
    protocolService.update(protocol.id, trimmed, protocol.description).subscribe(() => load())
  }

  const remove = () => {
    if (!protocol) return
    protocolService.remove(protocol.id).subscribe(() => {
      setConfirmDelete(false)
      navigate(ROUTE_PROTOCOLS).then()
    })
  }

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const addRow = () =>
    setDraft((d) => {
      const withNewRow = [...d, emptyRow()]
      // The new row lands last, so jump to that page to keep it in view.
      setPage(Math.floor((rules.length + withNewRow.length - 1) / ROWS_PER_PAGE))
      return withNewRow
    })
  const removeRow = (id: string) => setDraft((d) => d.filter((f) => f.id !== id))

  // Matches the id emptyRow() builds; a saved rule carries the backend id.
  const isDraftRule = (id: string) => id.startsWith('draft-')

  const editRule = (row: Row) =>
    setDraft((d) => (d.some((f) => f.id === row.id) ? d : [...d, { ...row, isNewRow: true }]))

  const deleteRule = (id: string) => protocolService.removeRule(id).subscribe(() => load())
  const changeRow = (id: string, field: keyof Row, value: string) =>
    setDraft((d) => d.map((f) => (f.id === id ? { ...f, [field]: value } : f)))

  // The table has no column for it, so the notification message is built from
  // the rules of the set.
  const messageFor = (rows: Row[]) =>
    rows.map((f) => `${f.parameter} ${operatorLabel(t, f.operator)} ${f.value}`).join('; ')

  const saveRules = async () => {
    if (draft.length === 0) return

    // Rules hang off a protocol, which is created elsewhere.
    if (!protocol) return

    // A rule with no value evaluates nothing: mark the row instead of saving.
    const incomplete = draft.filter((f) => !f.value.trim())
    if (incomplete.length > 0) {
      setRowsWithError(incomplete.map((f) => f.id))
      return
    }
    setRowsWithError([])

    setSaving(true)

    try {
      // Existing rules update in place; only new ones create a set.
      for (const f of draft.filter((x) => !isDraftRule(x.id))) {
        await protocolService
          .updateRule(f.id, {
            ruleSetID: rules.find((r) => r.id === f.id)?.rule.ruleSetID,
            parameter: f.parameter,
            operator: f.operator as RuleOperator,
            valueType: f.ruleType as RuleValueType,
            value: f.value,
            sampleSize: Number(f.sampleSize) || 1,
          })
          .toPromise()
      }

      // One set per distinct number in the "rule set" column.
      const bySet = new Map<string, Row[]>()
      draft
        .filter((x) => isDraftRule(x.id))
        .forEach((f) => {
          const k = f.ruleSet || '1'
          bySet.set(k, [...(bySet.get(k) || []), f])
        })

      for (const [number, rows] of Array.from(bySet.entries())) {
        const severity = (rows[0].severity as Severity) || Severity.Warning
        const rs = await protocolService
          .createRuleSet({
            protocolID: protocol.id,
            name: `${t('ruleSet')} ${number}`,
            notificationMessage: messageFor(rows),
            severity,
          })
          .toPromise()
        if (!rs) continue

        for (const f of rows) {
          await protocolService
            .createRule({
              ruleSetID: rs.id,
              parameter: f.parameter,
              operator: f.operator as RuleOperator,
              valueType: f.ruleType as RuleValueType,
              value: f.value,
              sampleSize: Number(f.sampleSize) || 1,
            })
            .toPromise()
        }
      }

      setDraft([])
      load()
    } finally {
      setSaving(false)
    }
  }

  // Saved rows first, then the ones being filled in, all in one table.
  // A saved row being edited is replaced by its draft.
  const editing = new Map(draft.filter((d) => !isDraftRule(d.id)).map((d) => [d.id, d]))

  const rows: Row[] = [
    ...rules
      .map((r) => ({
        id: r.id,
        isNewRow: false,
        ruleSet: String(r.ruleSetIndex),
        ruleNumber: `${r.ruleSetIndex}.${r.ruleIndex}`,
        parameter: r.rule.parameter,
        ruleType: r.rule.valueType || RuleValueType.Num,
        operator: r.rule.operator,
        value: r.rule.value,
        sampleSize: r.rule.sampleSize ? String(r.rule.sampleSize) : '',
        severity: r.severity,
      }))
      .map((f) => editing.get(f.id) ?? f),
    ...draft.filter((d) => isDraftRule(d.id)),
  ]

  const cell = (row: Row, field: keyof Row, options?: { value: string; text: string }[]) => {
    if (!row.isNewRow) {
      // Saved rows also show their label, not the technical value.
      const text =
        field === 'parameter'
          ? parameterLabel(t, row.parameter)
          : field === 'operator'
          ? operatorLabel(t, row.operator)
          : field === 'severity'
          ? severityLabel(t, row.severity)
          : field === 'ruleType'
          ? valueTypeLabel(t, row.ruleType)
          : String(row[field])
      return <Typography sx={{ fontSize: '14px' }}>{text}</Typography>
    }
    if (options) {
      return (
        <Select
          size="small"
          value={String(row[field])}
          onChange={(e) => changeRow(row.id, field, e.target.value as string)}
          sx={{ fontSize: '13px', minWidth: 110 }}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.text}
            </MenuItem>
          ))}
        </Select>
      )
    }
    const hasError = field === 'value' && rowsWithError.includes(row.id) && !row.value.trim()
    return (
      <TextField
        size="small"
        value={String(row[field])}
        onChange={(e) => changeRow(row.id, field, e.target.value)}
        error={hasError}
        helperText={hasError ? t('requiredField') : ''}
        sx={{ '& input': { fontSize: '13px', py: 0.75 } }}
      />
    )
  }

  const fields: Field<Row>[] = [
    {
      name: 'id',
      label: '',
      renderFunc: (f, row) =>
        row.isNewRow ? (
          <IconButton size="small" onClick={() => removeRow(row.id)} title={t('delete')}>
            <TrashIcon sx={{ fontSize: 16, color: 'var(--destructive, #e14c54)' }} />
          </IconButton>
        ) : (
          <Checkbox
            size="small"
            checked={selected.includes(row.id)}
            onChange={() => toggle(row.id)}
            sx={{ p: 0, color: 'var(--gray-border)' }}
          />
        ),
    },
    { name: 'ruleSet', label: t('ruleSet'), renderFunc: (f, row) => cell(row, 'ruleSet') },
    {
      name: 'ruleNumber',
      label: t('ruleNumber'),
      renderFunc: (f, row) => (
        <Typography sx={{ fontSize: '14px' }}>{row.ruleNumber || '—'}</Typography>
      ),
    },
    {
      name: 'parameter',
      label: t('parameter'),
      renderFunc: (f, row) =>
        cell(
          row,
          'parameter',
          MEASURED_PARAMETERS.map((p) => ({ value: p, text: parameterLabel(t, p) }))
        ),
    },
    {
      name: 'ruleType',
      label: t('ruleType'),
      renderFunc: (f, row) =>
        cell(row, 'ruleType', [
          { value: RuleValueType.Num, text: t('numValue') },
          { value: RuleValueType.String, text: t('stringValue') },
          { value: RuleValueType.List, text: t('listValue') },
        ]),
    },
    {
      name: 'operator',
      label: t('operator'),
      renderFunc: (f, row) =>
        row.isNewRow ? (
          cell(
            row,
            'operator',
            Object.values(RuleOperator).map((o) => ({ value: o, text: operatorLabel(t, o) }))
          )
        ) : (
          <Typography sx={{ fontSize: '14px' }}>{operatorLabel(t, row.operator)}</Typography>
        ),
    },
    { name: 'value', label: t('value'), renderFunc: (f, row) => cell(row, 'value') },
    {
      name: 'sampleSize',
      label: t('numberOfMeasurements'),
      renderFunc: (f, row) => cell(row, 'sampleSize'),
    },
    {
      name: 'severity',
      label: t('notificationType'),
      renderFunc: (f, row) =>
        row.isNewRow ? (
          cell(row, 'severity', [
            { value: Severity.Warning, text: severityLabel(t, Severity.Warning) },
            { value: Severity.Alarm, text: severityLabel(t, Severity.Alarm) },
          ])
        ) : (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 2,
              py: 0.5,
              borderRadius: '12px',
              fontSize: '13px',
              backgroundColor:
                row.severity === Severity.Alarm
                  ? 'rgba(206, 83, 71, 0.16)'
                  : 'rgba(214, 162, 67, 0.16)',
              color: row.severity === Severity.Alarm ? 'var(--error)' : 'var(--warning)',
            }}
          >
            {severityLabel(t, row.severity)}
          </Box>
        ),
    },
    {
      name: 'ruleNumber',
      label: t('actions'),
      renderFunc: (f, row) => (
        <Box display="flex" gap={0.5}>
          {row.isNewRow ? (
            <IconButton size="small" onClick={() => removeRow(row.id)} title={t('cancel')}>
              <BlockOutlinedIcon sx={{ fontSize: 16, color: 'var(--gray-medium)' }} />
            </IconButton>
          ) : (
            editable && (
              <>
                <IconButton size="small" onClick={() => editRule(row)} title={t('editIcon')}>
                  <EditOutlinedIcon sx={{ fontSize: 16, color: 'var(--gray-medium)' }} />
                </IconButton>
                <IconButton size="small" onClick={() => deleteRule(row.id)} title={t('delete')}>
                  <TrashIcon sx={{ fontSize: 16, color: 'var(--destructive, #e14c54)' }} />
                </IconButton>
              </>
            )
          )}
        </Box>
      ),
    },
  ]

  const totalRows = rules.length + draft.length

  return (
    <PageLayout>
      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <CustomModal
          title={t('delete')}
          warningText={t('irreversibleAction')}
          handleClose={() => setConfirmDelete(false)}
          handleSave={remove}
        />
      </Modal>

      {/* width al 100%: .innerCard de PageLayout centra a los hijos que no lo declaran */}
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
        mb={2}
        gap={2}
      >
        <Box>
          <Typography sx={{ fontSize: '22px', fontWeight: 600, color: 'var(--gray-medium)' }}>
            {t('protocolManagement')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'var(--gray-text)' }}>
            {t('protocolsSubtitle')}
          </Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        mb={2}
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {editingName ? (
            <TextField
              size="small"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === 'Enter' && saveName()}
              sx={{ '& fieldset': { borderRadius: '6px' } }}
            />
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: '15px',
                  color: protocol ? 'var(--gray-medium)' : 'var(--gray-border)',
                }}
              >
                {protocol?.name || t('selectProtocol')}
              </Typography>
              <IconButton size="small" onClick={openSelector} title={t('selectProtocol')}>
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}
        </Box>

        {/* The protocol declares its pathologies; patients inherit from theirs. */}
        {protocol && (
          <Box display="flex" alignItems="center" gap={1} flexGrow={1} maxWidth={520}>
            <Typography sx={{ fontSize: '14px', color: 'var(--gray-medium)' }}>
              {t('pathologies')}:
            </Typography>
            <Autocomplete
              multiple
              size="small"
              style={{ flexGrow: 1 }}
              disabled={!editable}
              options={catalog.map((c) => c.id)}
              getOptionLabel={(id) => t(catalog.find((c) => c.id === id)?.name || id)}
              value={pathologyIDs}
              onChange={(_, v) => savePathologies(v as string[])}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder={t('selectPathologies')} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((id, index) => (
                  <Chip
                    key={id}
                    size="small"
                    label={t(catalog.find((c) => c.id === id)?.name || id)}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>
        )}

        {/* The pencil lists existing protocols; creating one lives elsewhere. */}
        <Menu
          anchorEl={selectorAnchor}
          open={Boolean(selectorAnchor)}
          onClose={() => setSelectorAnchor(null)}
        >
          {available.length === 0 && <MenuItem disabled>{t('noProtocolsYet')}</MenuItem>}
          {available.map((p) => (
            <MenuItem key={p.id} selected={p.id === props.id} onClick={() => selectProtocol(p.id)}>
              {p.name}
            </MenuItem>
          ))}
          {protocol && editable && (
            <MenuItem
              onClick={() => {
                setSelectorAnchor(null)
                setEditingName(true)
              }}
              sx={{ borderTop: '1px solid var(--gray-border)' }}
            >
              {t('renameProtocol')}
            </MenuItem>
          )}
        </Menu>

        <Box display="flex" alignItems="center" gap={1.5}>
          {/* En el diseno los dos son pildoras rellenas: Disable en gris y Delete
              en rosa con el text y el icono en rojo. Los tonos salen de
              muestrear el mockup. */}
          <Button
            variant="contained"
            disableElevation
            startIcon={<BlockOutlinedIcon />}
            disabled={protocol?.status !== ProtocolStatus.Published}
            onClick={(e) => setDisableAnchor(e.currentTarget)}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              backgroundColor: '#d6d6d6',
              color: 'var(--gray-text)',
              '&:hover': { backgroundColor: '#c9c9c9' },
            }}
          >
            {t('disable')}
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<TrashIcon />}
            disabled={!protocol}
            onClick={() => setConfirmDelete(true)}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              backgroundColor: '#efacb3',
              color: 'var(--destructive, #e14c54)',
              '&:hover': { backgroundColor: '#e79aa3' },
            }}
          >
            {t('delete')}
          </Button>
        </Box>
      </Box>

      <Menu
        anchorEl={disableAnchor}
        open={Boolean(disableAnchor)}
        onClose={() => setDisableAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (protocol) protocolService.retire(protocol.id).subscribe(() => load())
            setDisableAnchor(null)
          }}
        >
          {t('retire')}
        </MenuItem>
      </Menu>

      {!protocol && (
        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
          {t('selectProtocolHint')}
        </Alert>
      )}

      {protocol && !editable && (
        <Alert severity="info" sx={{ mb: 2, width: '100%' }}>
          {t('protocolNotEditable')}
        </Alert>
      )}

      <AppTable
        rowKeyField="id"
        items={rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)}
        fields={fields}
      />

      {protocol && editable && (
        <Box
          onClick={addRow}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            borderTop: '1px solid var(--gray-border)',
            color: 'var(--gray-medium)',
            fontSize: '14px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
          {t('addRule')}
        </Box>
      )}

      {/* Only with nothing on screen: a half-filled row would contradict it. */}
      {rules.length === 0 && draft.length === 0 && (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">{t('noRulesYet')}</Typography>
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        width="100%"
        gap={1}
        mt={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {draft.length > 0 && (
            <Button
              variant="contained"
              disableElevation
              size="small"
              onClick={saveRules}
              disabled={saving}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                backgroundColor: 'var(--green-primary)',
                '&:hover': { backgroundColor: 'var(--green-dark)' },
              }}
            >
              {saving ? t('saving') : t('saveRules', { n: draft.length })}
            </Button>
          )}{' '}
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalRows / ROWS_PER_PAGE))}
          page={page + 1}
          onChange={(_, p) => setPage(p - 1)}
          shape="rounded"
          sx={{
            '& .Mui-selected': {
              backgroundColor: 'var(--green-primary) !important',
              color: 'var(--white)',
            },
          }}
        />
      </Box>

      {/* Rule sets are the only place rules can be created and removed, so they
          stay behind the "+" button. */}
      {editable && showEditors && protocol && (
        <Box width="100%" mt={3}>
          {(protocol.ruleSets || []).map((rs) => (
            <RuleEditor
              key={rs.id}
              ruleSet={rs}
              editable={editable}
              onAddRule={addRule}
              onRemoveRule={removeRule}
              onRemoveRuleSet={removeRuleSet}
            />
          ))}

          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography variant="subtitle1" mb={2}>
              {t('newRuleSet')}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <TextField
                size="small"
                label={t('ruleSetName')}
                value={ruleSetName}
                onChange={(e) => setRuleSetName(e.target.value)}
              />
              <TextField
                size="small"
                label={t('notificationMessage')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ minWidth: 320 }}
              />
              <Select
                size="small"
                value={severity}
                onChange={(e) => setSeverity(e.target.value as Severity)}
              >
                <MenuItem value={Severity.Warning}>{severityLabel(t, Severity.Warning)}</MenuItem>
                <MenuItem value={Severity.Alarm}>{severityLabel(t, Severity.Alarm)}</MenuItem>
              </Select>
              <Button
                variant="contained"
                onClick={addRuleSet}
                disabled={!ruleSetName.trim() || !message.trim()}
              >
                {t('addRuleSet')}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </PageLayout>
  )
}
